import { HubConnectionState, type HubConnection } from '@microsoft/signalr';
import {
  MANUAL_PING_START_DELAY_MS,
  SOCKET_INTERCEPT_DELAY_MS,
} from './SignalR.constants';
import { setupConnectionReceiveInterceptor } from './connectionInterceptors';
import { setupWebSocketInterceptor } from './connectionInterceptors';
import { getWebSocketFromConnection, logRegisteredHandlers } from './SignalR.internals';
import { startManualPing, type ManualPingRefs } from './manualPing';

export interface StartHubConnectionOptions {
  hubName: string;
  connection: HubConnection;
  pingRefs: ManualPingRefs;
  storeSocket: (hubName: string, socket: WebSocket) => void;
  removeSocket: (hubName: string) => void;
}

export const finalizeHubConnectionStart = async ({
  hubName,
  connection,
  pingRefs,
  storeSocket,
  removeSocket,
}: StartHubConnectionOptions): Promise<void> => {
  setupConnectionReceiveInterceptor(hubName, connection);

  await connection.start();

  const actualKeepAlive = connection.keepAliveIntervalInMilliseconds;
  console.log(
    `🔍 [${hubName}] Keepalive verification after start: ${actualKeepAlive}ms`,
  );

  if (actualKeepAlive !== 5000) {
    console.warn(
      `⚠️ [${hubName}] Keepalive was ${actualKeepAlive}ms, expected 5000ms. Attempting to fix...`,
    );
    try {
      connection.keepAliveIntervalInMilliseconds = 5000;
      console.log(
        `✅ [${hubName}] Keepalive reset to: ${connection.keepAliveIntervalInMilliseconds}ms`,
      );
    } catch (error) {
      console.error(`❌ [${hubName}] Failed to set keepalive:`, error);
    }
  }

  if (import.meta.env.DEV) {
    console.log(`\n✅ Connection [${hubName}] started. Inspecting handlers...`);
    setTimeout(() => logRegisteredHandlers(hubName, connection), 100);
  }

  setTimeout(() => {
    if (connection.state === HubConnectionState.Connected) {
      startManualPing(hubName, connection, pingRefs);
    }
  }, MANUAL_PING_START_DELAY_MS);

  setTimeout(() => {
    try {
      const socket = getWebSocketFromConnection(connection);
      if (!socket) {
        console.warn(`⚠️ [${hubName}] Could not find WebSocket in transport`);
        return;
      }

      storeSocket(hubName, socket);
      console.log(
        `✅ [${hubName}] WebSocket reference stored for ping mechanism (readyState: ${socket.readyState})`,
      );
      setupWebSocketInterceptor(hubName, socket, () => removeSocket(hubName));
    } catch (error) {
      console.warn(`⚠️ Could not intercept WebSocket for [${hubName}]:`, error);
    }
  }, SOCKET_INTERCEPT_DELAY_MS);

  console.log(`✅ SignalR connection [${hubName}] started successfully`);
};
